import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ArrowPathIcon,
  ShareIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/solid';
import { ConnectionMode } from '../api/vpn';

// Use ConnectionMode from vpn.ts as single source of truth (Issue #268)
// All connections are P2P - we NEVER relay VPN traffic through our servers.

/**
 * Display mode for connection type indicator
 */
export enum DisplayMode {
  COMPACT = 'compact',       // Icon + text
  FULL = 'full',            // Icon + text + badge in card
  BADGE_ONLY = 'badge_only', // Just the badge text
  ICON_ONLY = 'icon_only'    // Just the icon
}

interface ConnectionTypeConfig {
  displayName: string;
  description: string;
  detailedExplanation: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  bgColor: string;
  badge: string;
  speedPerformance: number;
  latencyPerformance: number;
  reliabilityPerformance: number;
}

const connectionTypeConfigs: Record<ConnectionMode, ConnectionTypeConfig> = {
  [ConnectionMode.DIRECT_P2P]: {
    displayName: 'Direct P2P',
    description: 'Direct peer-to-peer connection (fastest)',
    detailedExplanation: `Your devices are connected directly to each other without any intermediary servers. This provides the fastest possible connection with lowest latency.\n\nThis works when both devices are on the same network or can reach each other directly over the internet.`,
    icon: CheckCircleIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    badge: '⚡ Direct',
    speedPerformance: 1.0,
    latencyPerformance: 1.0,
    reliabilityPerformance: 0.7
  },
  [ConnectionMode.STUN_ASSISTED_P2P]: {
    displayName: 'STUN P2P',
    description: 'NAT traversal via STUN (fast)',
    detailedExplanation: `Your devices use STUN (Session Traversal Utilities for NAT) to establish a direct peer-to-peer connection through NAT routers. This is still P2P - no traffic goes through our servers.\n\nSTUN helps devices discover their public IP addresses and punch through NAT firewalls for direct connections.`,
    icon: ArrowPathIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    badge: '⚡ P2P',
    speedPerformance: 0.95,
    latencyPerformance: 0.9,
    reliabilityPerformance: 0.85
  },
  [ConnectionMode.CONNECTION_NODE_SIGNALING]: {
    displayName: 'Signaled P2P',
    description: 'WebSocket-coordinated P2P connection',
    detailedExplanation: `Your connection used WebSocket signaling through our connection nodes to establish the peer-to-peer link. The VPN traffic flows directly between devices - connection nodes only help coordinate the initial setup.\n\nThis provides reliable connectivity in complex network scenarios while maintaining peer-to-peer data transfer.`,
    icon: ShareIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    badge: '🔄 P2P',
    speedPerformance: 0.90,
    latencyPerformance: 0.85,
    reliabilityPerformance: 0.95
  },
  [ConnectionMode.UNKNOWN]: {
    displayName: 'Unknown',
    description: 'Connection type not yet determined',
    detailedExplanation: "The connection type hasn't been determined yet. Please wait for the connection to establish.",
    icon: QuestionMarkCircleIcon,
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    badge: '?',
    speedPerformance: 0,
    latencyPerformance: 0,
    reliabilityPerformance: 0
  }
};

interface ConnectionTypeIndicatorProps {
  connectionType: ConnectionMode;
  mode?: DisplayMode;
  showTooltip?: boolean;
  className?: string;
}

/**
 * Connection type indicator component
 *
 * Displays the connection type with icon, color-coding, and optional tooltip.
 * Supports multiple display modes (compact, full, badge, icon).
 *
 * Example usage:
 * ```tsx
 * <ConnectionTypeIndicator
 *   connectionType={ConnectionMode.DIRECT_P2P}
 *   mode={DisplayMode.COMPACT}
 *   showTooltip={true}
 * />
 * ```
 */
export const ConnectionTypeIndicator: React.FC<ConnectionTypeIndicatorProps> = ({
  connectionType,
  mode = DisplayMode.COMPACT,
  showTooltip = false,
  className = ''
}) => {
  const [showExplanation, setShowExplanation] = useState(false);
  const config = connectionTypeConfigs[connectionType];

  const renderContent = () => {
    switch (mode) {
      case DisplayMode.COMPACT:
        return (
          <div className={`flex items-center gap-2 ${className}`}>
            <config.icon className={`h-4 w-4 ${config.color}`} />
            <span className="text-sm">{config.displayName}</span>
            {showTooltip && (
              <button
                onClick={() => setShowExplanation(true)}
                className="text-primary-600 hover:text-primary-700"
                aria-label="More information"
              >
                <InformationCircleIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        );

      case DisplayMode.FULL:
        return (
          <div className={`flex items-center gap-3 ${config.bgColor} rounded-lg p-2 ${className}`}>
            <config.icon className={`h-5 w-5 ${config.color}`} />
            <div className="flex-1">
              <div className="font-medium text-sm">{config.displayName}</div>
              <div className="text-xs text-gray-600">{config.badge}</div>
            </div>
            {showTooltip && (
              <button
                onClick={() => setShowExplanation(true)}
                className="text-primary-600 hover:text-primary-700"
                aria-label="More information"
              >
                <InformationCircleIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        );

      case DisplayMode.BADGE_ONLY:
        return (
          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.color} ${config.bgColor} ${className}`}>
            {config.badge}
          </span>
        );

      case DisplayMode.ICON_ONLY:
        return <config.icon className={`h-5 w-5 ${config.color} ${className}`} />;
    }
  };

  return (
    <>
      <div
        role="status"
        aria-label={`${config.displayName} connection. ${config.description}`}
      >
        {renderContent()}
      </div>

      {showExplanation && (
        <ConnectionTypeExplanationModal
          connectionType={connectionType}
          onClose={() => setShowExplanation(false)}
        />
      )}
    </>
  );
};

interface ExplanationModalProps {
  connectionType: ConnectionMode;
  onClose: () => void;
}

/**
 * Explanation modal for connection types
 *
 * Shows detailed information about the connection type including
 * performance characteristics and comparisons with other types.
 */
const ConnectionTypeExplanationModal: React.FC<ExplanationModalProps> = ({
  connectionType,
  onClose
}) => {
  const config = connectionTypeConfigs[connectionType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <config.icon className={`h-12 w-12 ${config.color}`} />
            <div>
              <h2 className="text-2xl font-bold">{config.displayName}</h2>
              <p className="text-sm text-gray-600">{config.badge}</p>
            </div>
          </div>

          <hr className="my-6" />

          {/* Detailed explanation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">What does this mean?</h3>
            <p className="text-gray-700 whitespace-pre-line">{config.detailedExplanation}</p>
          </div>

          {/* Performance */}
          {connectionType !== ConnectionMode.UNKNOWN && (
            <>
              <hr className="my-6" />
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Performance</h3>
                <div className="space-y-4">
                  <PerformanceBar
                    label="Speed"
                    value={config.speedPerformance}
                    color={config.color}
                  />
                  <PerformanceBar
                    label="Latency"
                    value={config.latencyPerformance}
                    color={config.color}
                  />
                  <PerformanceBar
                    label="Reliability"
                    value={config.reliabilityPerformance}
                    color={config.color}
                  />
                </div>
              </div>
            </>
          )}

          {/* All connection types */}
          <hr className="my-6" />
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">All Connection Types</h3>
            <div className="space-y-3">
              {Object.entries(connectionTypeConfigs)
                .filter(([type]) => type !== ConnectionMode.UNKNOWN)
                .map(([type, typeConfig]) => (
                  <div key={type} className="flex items-start gap-3">
                    <typeConfig.icon className={`h-5 w-5 ${typeConfig.color} flex-shrink-0 mt-0.5`} />
                    <div>
                      <div className="font-medium text-sm">{typeConfig.displayName}</div>
                      <div className="text-xs text-gray-600">{typeConfig.description}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Close button */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface PerformanceBarProps {
  label: string;
  value: number;
  color: string;
}

const PerformanceBar: React.FC<PerformanceBarProps> = ({ label, value, color }) => {
  // Convert Tailwind color class to actual color for the progress bar
  const colorMap: Record<string, string> = {
    'text-green-600': 'bg-green-600',
    'text-blue-600': 'bg-blue-600',
    'text-purple-600': 'bg-purple-600'
  };

  const barColor = colorMap[color] || 'bg-gray-600';

  return (
    <div>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${value * 100}%` }}
        />
      </div>
    </div>
  );
};

export default ConnectionTypeIndicator;
